import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { Button, Stack, Typography } from "@mui/material";
import { CharacteristicsEmptyList } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook } from "hooks";
import { capitalize, isArray, startCase } from "lodash";
import { useDealActions, useDealSelector } from "store";
import { ICharacteristicView, ICharacteristicViewData } from "store/types";

interface ICharacteristicViewDataProps {
  list: ICharacteristicViewData;
  refetch: () => Promise<void>;
  /** Field names to highlight as missing (for required-for-stage flow) */
  missingFieldNames?: string[];
}

export const CharacteristicsList: React.FC<ICharacteristicViewDataProps> = ({ list, refetch, missingFieldNames }) => {
  const { setCharacteristicsManageAction } = useDealActions();
  const firstMissingRef = useRef<HTMLLIElement | null>(null);

  const { id, title } = useDealSelector();
  const formatDate = useFormatDateHook();

  const missingNamesLower = useMemo(
    () => new Set((missingFieldNames ?? []).map((n) => n.trim().toLowerCase())),
    [missingFieldNames]
  );

  useEffect(() => {
    if (missingFieldNames?.length && firstMissingRef.current) {
      firstMissingRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [missingFieldNames?.length, list]);

  const isEmpty = useMemo(() => {
    return Object.values(list).every((value) => value === null);
  }, [list]);

  const dataKeys = useMemo(() => Object.keys(list) as Array<keyof ICharacteristicViewData>, [list]);

  const columnsCharacteristics = useMemo(() => {
    const data = { columnsCount: 0, hasMoreThanSeven: [] as string[] };
    for (const key in list) {
      if (list[key as keyof ICharacteristicViewData] && list[key as keyof ICharacteristicViewData].length) {
        data.columnsCount++;
      }
      if (list[key as keyof ICharacteristicViewData] && list[key as keyof ICharacteristicViewData].length > 7) {
        data.hasMoreThanSeven.push(key);
      }
    }
    return data;
  }, [list]);

  const renderValue = useCallback((item: ICharacteristicView) => {
    if (!item.value) return "N/A";
    if (item.data_type && item.data_type === "date") {
      return formatDate({ date: item.value });
    }
    if (item.prefix) {
      return item.prefix === "%" ? `${item.value}${item.prefix}` : `${item.prefix}${item.value}`;
    }
    return item.value;
  }, []);

  const characteristicBuilder = (list: ICharacteristicViewData) => {
    // if 2 characteristics but one has more than 7 items
    if (
      columnsCharacteristics.columnsCount < 3 &&
      columnsCharacteristics.hasMoreThanSeven.length === columnsCharacteristics.columnsCount - 1
    ) {
      const splitData = (data: ICharacteristicViewData, splitIndex: number): ICharacteristicViewData[] => {
        return Object.entries(data).reduce((result: ICharacteristicViewData[], [key, array]) => {
          const partialResult: Partial<ICharacteristicViewData> = {};

          if (array && array.length > splitIndex) {
            partialResult[key as keyof ICharacteristicViewData] = array.slice(0, splitIndex);
            result.push({ ...partialResult } as ICharacteristicViewData);

            partialResult[key as keyof ICharacteristicViewData] = array.slice(splitIndex);
            result.push({ ...partialResult } as ICharacteristicViewData);
          } else {
            partialResult[key as keyof ICharacteristicViewData] = array;
            result.push({ ...partialResult } as ICharacteristicViewData);
          }

          return result;
        }, []);
      };
      const splitedData = splitData(list, 7);
      return (
        <Stack>
          <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35" }}>Full Characteristics </Typography>
          <Stack direction="row" justifyContent="space-between" sx={{ overflowY: "auto", maxHeight: "165px" }}>
            {splitedData.map((item, idx) => {
              const key = Object.keys(item)[0];
              const values = item[key as keyof ICharacteristicViewData];
              if (!values) return;
              return (
                <Stack
                  key={idx}
                  sx={{
                    width: "33%",
                    padding: "0px"
                  }}
                >
                  <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35" }}>
                    {startCase(key)}
                  </Typography>
                  <ul>
                    {(values as ICharacteristicView[]).map((item, idx) => (
                      <li key={idx}>
                        <Stack direction="row" justifyContent="flex-start" alignItems="center">
                          <Typography
                            sx={{
                              fontSize: "15px",
                              fontWeight: "400",
                              color: "#496682",
                              marginRight: "5px",
                              textWrap: "nowrap"
                            }}
                          >
                            {startCase(item.title)}:
                          </Typography>
                          <MaterialTooltip
                            title={isArray(item.value) ? item.value.map((value) => value).join(", ") : item.value}
                            placement="top"
                          >
                            <Typography
                              sx={{
                                fontSize: "15px",
                                fontWeight: "600",
                                color: "#071F35",
                                overflow: "hidden",
                                textWrap: "nowrap",
                                textOverflow: "ellipsis"
                              }}
                            >
                              {renderValue(item)}
                            </Typography>
                          </MaterialTooltip>
                        </Stack>
                      </li>
                    ))}
                  </ul>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      );
    }

    if (columnsCharacteristics.columnsCount === 1 && columnsCharacteristics.hasMoreThanSeven.length) {
      return (
        <Stack>
          <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35" }}>Full characteristics </Typography>
          <Stack gap="20px">
            {dataKeys.map((key, idx) => {
              if (!list[key]) return;
              return (
                <Stack key={idx}>
                  <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35", textAlign: "center" }}>
                    {capitalize(key)}
                  </Typography>
                  <Stack
                    sx={{
                      maxHeight: "165px",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      overflowY: "auto",
                      gap: "2px",
                      justifyItems: "center"
                    }}
                  >
                    {(list[key] as ICharacteristicView[]).map((item) => (
                      <Stack
                        key={item.title}
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        gap="3px"
                        sx={{ width: "280px", maxWidth: "400px" }}
                      >
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: "400",
                            color: "#496682",
                            textWrap: "nowrap"
                          }}
                        >
                          {item.title}:
                        </Typography>
                        <MaterialTooltip title={item.value} placement="top">
                          <Typography
                            sx={{
                              fontSize: "15px",
                              fontWeight: "400",
                              color: "#071F35",
                              overflow: "hidden",
                              textWrap: "nowrap",
                              textOverflow: "ellipsis",
                              maxWidth: "300px"
                            }}
                          >
                            {renderValue(item)}
                          </Typography>
                        </MaterialTooltip>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      );
    }
    // if 2 characteristics and each column has less than 7 items
    // if (columnsCharacteristics.columnsCount < 3 && !columnsCharacteristics.hasMoreThanSeven.length) {
    //   return (
    //     <Stack gap="20px">
    //       {dataKeys.map((key, idx) => {
    //         if (!list[key]) return;

    //         return (
    //           <Stack key={idx}>
    //             <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35" }}>
    //               {capitalize(key)}
    //             </Typography>
    //             <Stack direction="row" flexWrap="wrap" sx={{ maxHeight: "45px", overflow: "auto" }}>
    //               {list[key].map((item) => (
    //                 <Stack
    //                   key={item.title}
    //                   direction="row"
    //                   justifyContent="flex-start"
    //                   alignItems="center"
    //                   sx={{ marginRight: "20px" }}
    //                 >
    //                   <Typography
    //                     sx={{
    //                       fontSize: "15px",
    //                       fontWeight: "400",
    //                       color: "#496682",
    //                       marginRight: "5px",
    //                       textWrap: "nowrap"
    //                     }}
    //                   >
    //                     {item.title}:
    //                   </Typography>
    //                   <MaterialTooltip title={item.value} placement="top">
    //                     <Typography
    //                       sx={{
    //                         fontSize: "15px",
    //                         fontWeight: "400",
    //                         color: "#071F35",
    //                         overflow: "hidden",
    //                         textWrap: "nowrap",
    //                         textOverflow: "ellipsis",
    //                         maxWidth: "300px"
    //                       }}
    //                     >
    //                       {item.value ? item.value : "N/A"}
    //                     </Typography>
    //                   </MaterialTooltip>
    //                 </Stack>
    //               ))}
    //             </Stack>
    //           </Stack>
    //         );
    //       })}
    //     </Stack>
    //   );
    // }
    // if 3 characteristics columns with scroll

    return (
      <Stack>
        <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35" }}>Full Characteristics </Typography>
        <Stack direction="row" justifyContent="space-between" sx={{ overflowY: "auto", maxHeight: "165px" }}>
          {dataKeys.map((key) => {
            if (!list[key]) return;

            // Handle contact as ICharacteristicView[][]
            if (key === "contact") {
              const contactGroups = list[key] as ICharacteristicView[][];
              return (
                <Stack
                  key={key}
                  sx={{
                    width: "33%",
                    padding: "0px",
                    ul: {
                      pl: "25px",
                      pr: "25px"
                    }
                  }}
                >
                  <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35", position: "sticky" }}>
                    {startCase(key)}
                  </Typography>
                  {contactGroups.map((contactGroup, groupIdx) => (
                    <Stack key={groupIdx} sx={{ mb: groupIdx < contactGroups.length - 1 ? "20px" : 0 }}>
                      {contentBuilder(contactGroup)}
                    </Stack>
                  ))}
                </Stack>
              );
            }

            // Handle other fields as ICharacteristicView[]
            return (
              <Stack
                key={key}
                sx={{
                  width: "33%",
                  padding: "0px",
                  ul: {
                    pl: "25px",
                    pr: "25px"
                  }
                }}
              >
                <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35", position: "sticky" }}>
                  {startCase(key)}
                </Typography>
                {contentBuilder(list[key])}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    );
  };

  const contentBuilder = (content: ICharacteristicView[]) => {
    let firstMissingFound = false;
    return (
      <ul>
        {content.map((item) => {
          const isMissing = missingNamesLower.has((item.title || "").trim().toLowerCase());
          const refCallback = (el: HTMLLIElement | null) => {
            if (el) {
              firstMissingRef.current = el;
              firstMissingFound = true;
            }
          };

          return (
            <li
              key={item.title}
              {...(isMissing && !firstMissingFound && { ref: refCallback })}
              {...(isMissing && {
                style: {
                  backgroundColor: "rgba(255, 193, 7, 0.2)",
                  borderRadius: "4px",
                  margin: "2px 0"
                }
              })}
            >
              <Stack direction="row" justifyContent="flex-start" alignItems="center">
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: "400",
                    color: "#496682",
                    marginRight: "5px",
                    textWrap: "nowrap"
                  }}
                >
                  {item.title}:
                </Typography>
                <MaterialTooltip title={item.value} placement="top">
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontWeight: "400",
                      color: "#071F35",
                      overflow: "hidden",
                      textWrap: "nowrap",
                      textOverflow: "ellipsis",
                      maxWidth: "300px"
                    }}
                  >
                    {renderValue(item)}
                  </Typography>
                </MaterialTooltip>
              </Stack>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Stack sx={{ height: "100%", padding: "0 16px 16px 16px" }}>
      {isEmpty ? (
        <>
          <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35" }}>Full Characteristics </Typography>
          <Stack sx={{ height: "100%" }} justifyContent="space-between" alignItems="center">
            <CharacteristicsEmptyList sx={{ color: "#92A7BC", width: "70px", height: "70px" }} />
            <Typography sx={{ fontSize: "15px", fontWeight: "400", color: "#496682", paddingBottom: "20px" }}>
              No characteristics selected yet
            </Typography>
            <Button
              onClick={() =>
                setCharacteristicsManageAction({
                  dealName: title,
                  dealId: id,
                  viewOnly: false,
                  onSubmitSuccess: refetch
                })
              }
              type="submit"
              variant="contained"
            >
              Select Characteristics
            </Button>
          </Stack>
        </>
      ) : (
        <>{characteristicBuilder(list)}</>
      )}
    </Stack>
  );
};
